import { z } from "zod";
import { DiscrepancySchema } from "./verification.js";
// ─── Enums ─────────────────────────────────────────────────────────────────
/**
 * Уровень детализации сверки.
 *
 * `"unified"` — единый полный отчёт (RC-G1 + RC-P1..P10), default since 026.
 * `"position"` / `"grand-total"` — deprecated; сохраняются для исторических
 * наборов и интерпретируются pipeline-ом как `"unified"` при re-run.
 *
 * Sprint 3 / A4: значение `"aggregate-by-work-kind"` удалено (clean-cut,
 * прод 016 не имеет таких записей; в dev/test БД миграция 0022 конвертирует
 * legacy строки в `"unified"`).
 *
 * См. specs/026-unified-report/plan.md, docs/code-review/2026-05-07-fix-plan-tasks.md.
 */
export const ReconciliationLevelEnum = z.enum(["unified", "position", "grand-total"]);
/** Режим сверки. По методологии § 4.4. */
export const ReconciliationModeEnum = z.enum(["volume", "rate", "cost"]);
/**
 * Жизненный цикл набора сверки (FR-006a).
 *
 * `ready` упразднён вместе со слиянием кнопок «Подготовить к запуску»
 * и «Запустить сверку» — `POST /run` теперь сам делает inline pre-flight,
 * промежуточное состояние больше не нужно. Миграция 0025 конвертирует
 * исторические `ready` → `draft`.
 *
 * `running`/`completed`/`failed` — зеркало последнего прогона (см.
 * `ReconciliationRun.status`); проставляются в той же транзакции, что
 * меняет `run.status` (см. `apps/api/src/services/reconciliation-runs/`).
 */
export const ReconciliationStatusEnum = z.enum([
    "draft",
    "running",
    "completed",
    "failed",
]);
/** Роль файла в наборе сверки (FR-003). */
export const ReconciliationSetFileRoleEnum = z.enum(["work_order", "act"]);
// ─── Базовые schemas ───────────────────────────────────────────────────────
/** Краткое представление файла (для эндпойнтов eligible-files и в составе набора). */
export const FileRefSchema = z.object({
    id: z.number().int().positive(),
    originalName: z.string(),
    sheetName: z.string().nullable(),
    objectName: z.string().nullable(),
    periodMonth: z.number().nullable(),
    periodYear: z.number().nullable(),
    createdAt: z.string(),
});
/** Полное представление набора сверки (для GET и ответов на mutations). */
export const ReconciliationSetSchema = z.object({
    id: z.number(),
    name: z.string(),
    level: ReconciliationLevelEnum,
    modes: z.array(ReconciliationModeEnum).min(1),
    status: ReconciliationStatusEnum,
    createdBy: z.number().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    files: z.object({
        workOrders: z.array(FileRefSchema),
        acts: z.array(FileRefSchema),
    }),
});
// ─── Request/Response schemas ──────────────────────────────────────────────
/**
 * POST /api/reconciliation/sets — тело запроса (FR-001).
 *
 * Фича 026:
 * - `level` стало опциональным с дефолтом `"unified"`. Старые клиенты,
 *   передающие `"position"` / `"grand-total"`, продолжают работать — pipeline
 *   всё равно выдаёт unified-отчёт.
 * - `modes` стало опциональным с дефолтом `["cost", "volume"]` (Wave 6).
 *   Это ядро sanity-сверки: грант-тотал + per-position quantity. `rate`
 *   (RC-P6) — продвинутая опция, явно включаемая в UI.
 *
 * См. plan.md.
 */
export const CreateReconciliationSetRequestSchema = z.object({
    name: z.string().min(1).max(200),
    level: ReconciliationLevelEnum.optional().default("unified"),
    modes: z
        .array(ReconciliationModeEnum)
        .min(1)
        .refine((arr) => new Set(arr).size === arr.length, {
        message: "modes must be unique (no duplicates)",
    })
        .optional()
        .default(["cost", "volume"]),
    files: z.object({
        workOrders: z
            .array(z.number().int().positive())
            .min(1)
            .refine((arr) => new Set(arr).size === arr.length, {
            message: "workOrders file ids must be unique",
        }),
        acts: z
            .array(z.number().int().positive())
            .min(1)
            .refine((arr) => new Set(arr).size === arr.length, {
            message: "acts file ids must be unique",
        }),
    }),
});
/**
 * PATCH /api/reconciliation/sets/:setId — тело запроса (FR-006b).
 *
 * Replace-all семантика: каждое присутствующее поле полностью заменяет текущее.
 * Минимум одно поле обязательно.
 */
export const UpdateReconciliationSetRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    level: ReconciliationLevelEnum.optional(),
    modes: z
        .array(ReconciliationModeEnum)
        .min(1)
        .refine((arr) => new Set(arr).size === arr.length, {
        message: "modes must be unique (no duplicates)",
    })
        .optional(),
    files: z
        .object({
        workOrders: z
            .array(z.number().int().positive())
            .min(1)
            .refine((arr) => new Set(arr).size === arr.length, {
            message: "workOrders file ids must be unique",
        }),
        acts: z
            .array(z.number().int().positive())
            .min(1)
            .refine((arr) => new Set(arr).size === arr.length, {
            message: "acts file ids must be unique",
        }),
    })
        .optional(),
})
    .refine((v) => v.name !== undefined ||
    v.level !== undefined ||
    v.modes !== undefined ||
    v.files !== undefined, { message: "Хотя бы одно поле должно быть передано в PATCH-запросе" });
/** GET /api/reconciliation/sets — query parameters. */
export const ListReconciliationSetsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    status: ReconciliationStatusEnum.optional(),
    createdBy: z.coerce.number().int().positive().optional(),
});
/** GET /api/reconciliation/sets — response. */
export const ListReconciliationSetsResponseSchema = z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    items: z.array(ReconciliationSetSchema),
});
/** GET /api/reconciliation/eligible-files/:role — path parameters. */
export const EligibleFilesPathSchema = z.object({
    role: ReconciliationSetFileRoleEnum,
});
/** GET /api/reconciliation/eligible-files/:role — response. */
export const EligibleFilesResponseSchema = z.object({
    role: ReconciliationSetFileRoleEnum,
    total: z.number(),
    files: z.array(FileRefSchema),
});
/** POST /api/reconciliation/sets/:setId/validate — response (FR-009). */
export const PreflightResultSchema = z.object({
    ok: z.boolean(),
    issues: z.array(z.string()),
    /** Предупреждения: запуск не блокируют, но видны пользователю (T-A5.4). */
    warnings: z.array(z.string()).default([]),
});
/**
 * POST /api/reconciliation/sets/:setId/prepare — response.
 *
 * До удаления статуса `ready` ответ нёс ещё `status` — финальный статус
 * набора после prepare-перехода. После того как prepare стал read-only
 * валидацией (статус не меняется), поле `status` упразднено; UI смотрит
 * только на `ok` + `issues` + `warnings`.
 */
export const PrepareResultSchema = z.object({
    ok: z.boolean(),
    issues: z.array(z.string()),
    /** Предупреждения: запуск не блокируют, но видны пользователю (T-A5.4). */
    warnings: z.array(z.string()).default([]),
});
// ============================================================================
// Этап 5 (фича 022): прогон сверки и отчёт
// ============================================================================
/** Состояние прогона сверки (FR-035, research.md R6). */
export const RunStatusSchema = z.enum([
    "pending",
    "running",
    "completed",
    "failed",
]);
/** Снимок параметров прогона (research.md R2, FR-030). Иммутабельный. */
export const ParametersSnapshotSchema = z.object({
    schemaVersion: z.literal(1),
    set: z.object({
        id: z.number().int().positive(),
        name: z.string(),
        level: ReconciliationLevelEnum,
        modes: z.array(ReconciliationModeEnum),
    }),
    files: z.object({
        workOrders: z.array(z.object({
            fileId: z.number().int().positive(),
            originalName: z.string(),
            sheetName: z.string().nullable(),
        })),
        acts: z.array(z.object({
            fileId: z.number().int().positive(),
            originalName: z.string(),
            sheetName: z.string().nullable(),
        })),
    }),
    featureFlags: z.object({
        matchExtraTimeUnits: z.boolean(),
        useFileMetadata: z.boolean(),
    }),
    ruleConfig: z.record(z.unknown()),
    engineVersion: z.string(),
});
/** Структурированная ошибка прогона (для status=failed). */
export const RunErrorMessageSchema = z.object({
    phase: z.enum(["loading-data", "rule-execution", "persistence", "task-queue"]),
    ruleId: z.string().nullable(),
    message: z.string(),
    stack: z.string().optional(),
});
/** Запись прогона в reconciliation_runs (FR-030). */
export const RunRecordSchema = z.object({
    id: z.string().uuid(),
    setId: z.number().int().positive(),
    userId: z.number().int().positive().nullable(),
    taskId: z.string(),
    status: RunStatusSchema,
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    discrepancyCount: z.number().int().nonnegative().nullable(),
    severityBreakdown: z
        .object({ error: z.number().int().nonnegative(), warning: z.number().int().nonnegative() })
        .nullable(),
    modes: z.array(ReconciliationModeEnum),
    parametersSnapshot: ParametersSnapshotSchema,
    errorMessage: RunErrorMessageSchema.nullable(),
});
/** POST /api/reconciliation/sets/:setId/run — обычный успешный ответ (202, FR-022). */
export const RunStartResponseSchema = z.object({
    taskId: z.string(),
    runId: z.string().uuid(),
});
/** POST /api/reconciliation/sets/:setId/run — 409 при concurrent run (FR-022a). */
export const RunInProgressResponseSchema = z.object({
    status: z.literal("RUN_IN_PROGRESS"),
    existingRunId: z.string().uuid(),
    taskId: z.string(),
});
/** Защитный ответ при превышении лимита Discrepancy (FR-020, US5). */
export const LimitExceededResponseSchema = z.object({
    status: z.literal("LIMIT_EXCEEDED"),
    totalCount: z.number().int().positive(),
    severityBreakdown: z.object({
        error: z.number().int().nonnegative(),
        warning: z.number().int().nonnegative(),
    }),
    runId: z.string().uuid(),
});
/** GET /api/reconciliation/runs/:runId — обычный отчёт (FR-033). */
export const RunReportSchema = z.object({
    run: RunRecordSchema,
    discrepancies: z.array(DiscrepancySchema),
});
/** GET /api/reconciliation/runs/:runId — отчёт при превышении лимита. */
export const RunReportLimitExceededSchema = z.object({
    run: RunRecordSchema,
    status: z.literal("LIMIT_EXCEEDED"),
    totalCount: z.number().int().positive(),
    severityBreakdown: z.object({
        error: z.number().int().nonnegative(),
        warning: z.number().int().nonnegative(),
    }),
});
/** Элемент списка прогонов (без discrepancies, для GET /sets/:setId/runs, FR-034). */
export const RunListItemSchema = z.object({
    id: z.string().uuid(),
    setId: z.number().int().positive(),
    userId: z.number().int().positive().nullable(),
    status: RunStatusSchema,
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    discrepancyCount: z.number().int().nullable(),
    severityBreakdown: z
        .object({ error: z.number().int().nonnegative(), warning: z.number().int().nonnegative() })
        .nullable(),
});
/** GET /api/reconciliation/sets/:setId/runs — response (FR-034). */
export const RunListResponseSchema = z.object({
    runs: z.array(RunListItemSchema),
});
//# sourceMappingURL=reconciliation.js.map